import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, errMsg } from "@/lib/api";
import { trackView } from "@/lib/trackView";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, FileText, MessageSquare, ArrowLeft, ExternalLink, Building2 } from "lucide-react";

export default function VendorDetail() {
  const { vendorId } = useParams();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    trackView("vendor", vendorId);
    api.get(`/vendors/${vendorId}`)
      .then((r) => setVendor(r.data))
      .catch(() => setError(true));
  }, [vendorId]);

  const sendRequest = async () => {
    setSending(true);
    try {
      await api.post(`/vendors/${vendorId}/request-info`, { message });
      toast.success("Your request has been sent to the vendor!");
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(errMsg(e, "Could not send request"));
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="vendor-not-found">
        <h1 className="text-2xl font-black text-forest mb-2">Vendor not found</h1>
        <Link to="/vendors" className="text-terracotta-dark font-bold hover:underline">Back to directory</Link>
      </div>
    );
  }
  if (!vendor) return <div className="text-center py-20 text-forest/50">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="vendor-detail-page">
      <Link to="/vendors" className="inline-flex items-center gap-1 text-sm font-bold text-forest/60 hover:text-terracotta-dark mb-6" data-testid="back-to-vendors">
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm mb-6">
        <div className="h-44 bg-sage-light flex items-center justify-center overflow-hidden">
          {vendor.logo_path ? (
            <img src={fileUrl(vendor.logo_path)} alt={vendor.business_name} className="w-full h-full object-contain" />
          ) : (
            <img src="/logo.png" alt="Compass Kids logo" className="max-h-28 max-w-[70%] object-contain opacity-80" data-testid="vendor-detail-logo-fallback" />
          )}
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-forest" data-testid="vendor-name">{vendor.business_name}</h1>
              {vendor.tagline && <p className="text-forest/60 mt-1">{vendor.tagline}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(vendor.categories || []).map((c) => (
                  <span key={c} className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full">{c}</span>
                ))}
              </div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md" data-testid="request-info-btn">
                  <MessageSquare className="w-4 h-4 mr-2" /> Request Info
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl bg-cream" data-testid="request-info-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-forest">Request info from {vendor.business_name}</DialogTitle>
                  <DialogDescription className="sr-only">Send a message to this vendor to request more information.</DialogDescription>
                </DialogHeader>
                {user && user.role === "parent" ? (
                  <div className="space-y-4">
                    <p className="text-sm text-forest/60">Your name and email will be shared with the vendor so they can reply.</p>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Hi! I'd love to know more about your classes for my 8-year-old..."
                      rows={5}
                      className="rounded-xl bg-white border-sage/30"
                      data-testid="request-message-input"
                    />
                    <Button onClick={sendRequest} disabled={sending || message.trim().length < 5} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="send-request-btn">
                      {sending ? "Sending..." : "Send request"}
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-forest/70 space-y-3">
                    <p>Please sign in as a parent to contact this vendor.</p>
                    <Link to="/login"><Button className="rounded-full bg-sage hover:bg-sage-dark text-white w-full" data-testid="dialog-login-btn">Sign in</Button></Link>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {vendor.description && <p className="text-forest/75 leading-relaxed mt-5 whitespace-pre-line">{vendor.description}</p>}

          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {vendor.location && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-sage-dark hover:text-terracotta-dark hover:underline transition-colors"
                data-testid="vendor-detail-location-link"
                aria-label={`Open ${vendor.location} in Google Maps`}
              >
                <MapPin className="w-4 h-4 text-sage-dark shrink-0" /> {vendor.location}
              </a>
            )}
            {vendor.phone && (
              <div className="flex items-center gap-2 text-sm text-forest/70"><Phone className="w-4 h-4 text-sage-dark shrink-0" /> {vendor.phone}</div>
            )}
            {vendor.contact_email && (
              <div className="flex items-center gap-2 text-sm text-forest/70"><Mail className="w-4 h-4 text-sage-dark shrink-0" /> {vendor.contact_email}</div>
            )}
            {vendor.website && (
              <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-terracotta-dark font-semibold hover:underline">
                <Globe className="w-4 h-4 shrink-0" /> Visit website <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>

      {vendor.has_multiple_branches && (vendor.branches || []).length > 0 && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 mb-6" data-testid="vendor-branches">
          <h2 className="text-xl font-black text-forest mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-sage-dark" /> Branches
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {vendor.branches.map((b, i) => {
              const addr = [b.address, b.city, b.province].filter(Boolean).join(", ");
              return (
                <div key={i} className="bg-softstone/70 rounded-2xl p-4" data-testid={`vendor-branch-card-${i}`}>
                  {b.name && (
                    <h3 className="font-heading font-bold text-forest" data-testid={`vendor-branch-card-name-${i}`}>{b.name}</h3>
                  )}
                  <div className="mt-2 space-y-1.5 text-sm">
                    {addr && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-2 text-sage-dark hover:text-terracotta-dark hover:underline transition-colors"
                        data-testid={`vendor-branch-card-address-${i}`}
                        aria-label={`Open ${addr} in Google Maps`}
                      >
                        <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{addr}</span>
                      </a>
                    )}
                    {b.phone && (
                      <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-forest/70 hover:text-terracotta-dark" data-testid={`vendor-branch-card-phone-${i}`}>
                        <Phone className="w-4 h-4 shrink-0" /> {b.phone}
                      </a>
                    )}
                    {b.email && (
                      <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-forest/70 hover:text-terracotta-dark break-all" data-testid={`vendor-branch-card-email-${i}`}>
                        <Mail className="w-4 h-4 shrink-0" /> {b.email}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(vendor.services || []).length > 0 && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 mb-6" data-testid="vendor-services">
          <h2 className="text-xl font-black text-forest mb-4">Services</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {vendor.services.map((s, i) => (
              <div key={i} className="bg-softstone/70 rounded-2xl p-4" data-testid={`service-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading font-bold text-forest">{s.name}</h3>
                  {s.price && <span className="text-xs font-bold text-sage-dark bg-sage-light px-2 py-1 rounded-full shrink-0">{s.price}</span>}
                </div>
                {s.description && <p className="text-sm text-forest/60 mt-1">{s.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(vendor.image_paths || []).length > 0 && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 mb-6" data-testid="vendor-gallery">
          <h2 className="text-xl font-black text-forest mb-4">Photos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {vendor.image_paths.map((p) => (
              <img key={p} src={fileUrl(p)} alt="Vendor photo" className="w-full h-40 object-contain rounded-2xl bg-sage-light" />
            ))}
          </div>
        </div>
      )}

      {(vendor.flyer_paths || []).length > 0 && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8" data-testid="vendor-flyers">
          <h2 className="text-xl font-black text-forest mb-4">Flyers & brochures</h2>
          <div className="space-y-2">
            {vendor.flyer_paths.map((f) => (
              <a key={f.path} href={fileUrl(f.path)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 bg-softstone/70 hover:bg-sage-light rounded-2xl p-4 transition-colors" data-testid={`flyer-${f.path}`}>
                <FileText className="w-5 h-5 text-terracotta-dark shrink-0" />
                <span className="text-sm font-semibold text-forest">{f.name}</span>
                <ExternalLink className="w-3.5 h-3.5 text-forest/40 ml-auto" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
